/**
 * Hiệu ứng âm thanh cho game "Chế tạo Robot" — tổng hợp bằng Web Audio API.
 * Không tải file ngoài; AudioContext chỉ tạo sau thao tác đầu tiên (autoplay).
 */
export type RobotSfx = "click" | "correct" | "wrong" | "assemble" | "finish";

let ctx: AudioContext | null = null;
let muted = false;

function ensureCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  }
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

function note(ac: AudioContext, freq: number, startAt: number, dur: number, type: OscillatorType, peak: number) {
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, startAt);
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(peak, startAt + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + dur);
  osc.connect(gain).connect(ac.destination);
  osc.start(startAt);
  osc.stop(startAt + dur + 0.02);
}

function sweep(ac: AudioContext, from: number, to: number, startAt: number, dur: number, peak: number) {
  const osc = ac.createOscillator();
  const gain = ac.createGain();
  osc.type = "square";
  osc.frequency.setValueAtTime(from, startAt);
  osc.frequency.exponentialRampToValueAtTime(to, startAt + dur);
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(peak, startAt + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + dur);
  osc.connect(gain).connect(ac.destination);
  osc.start(startAt);
  osc.stop(startAt + dur + 0.02);
}

export const robotSound = {
  get muted() {
    return muted;
  },
  setMuted(v: boolean) {
    muted = v;
  },
  toggle(): boolean {
    muted = !muted;
    return muted;
  },
  unlock() {
    ensureCtx();
  },
  play(name: RobotSfx) {
    if (muted) return;
    const ac = ensureCtx();
    if (!ac) return;
    const t = ac.currentTime;
    switch (name) {
      case "click":
        note(ac, 320, t, 0.09, "triangle", 0.18);
        break;
      case "correct":
        note(ac, 523.25, t, 0.18, "sine", 0.22);
        note(ac, 659.25, t + 0.08, 0.18, "sine", 0.22);
        note(ac, 783.99, t + 0.16, 0.26, "sine", 0.24);
        break;
      case "wrong":
        note(ac, 300, t, 0.18, "sawtooth", 0.16);
        note(ac, 200, t + 0.14, 0.28, "sawtooth", 0.16);
        break;
      case "assemble": // tiếng "ráp" bộ phận robot
        sweep(ac, 260, 620, t, 0.14, 0.2);
        note(ac, 880, t + 0.12, 0.1, "square", 0.16);
        break;
      case "finish":
        note(ac, 523.25, t, 0.16, "square", 0.16);
        note(ac, 659.25, t + 0.12, 0.16, "square", 0.16);
        note(ac, 783.99, t + 0.24, 0.16, "square", 0.16);
        note(ac, 1046.5, t + 0.36, 0.4, "square", 0.2);
        break;
    }
  },
};
